import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Check, Loader2, Search, Share2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ROLE_LABELS } from "@/lib/rbac";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getOrgLabel = (org) => org?.name ?? org?.organization_name ?? org?.label ?? "";
const getOrgId = (org) => String(org?.id ?? org?.organization_id ?? "");

const isSameAuthUser = (authUser, user) => {
  if (!authUser || !user) return false;
  if (authUser.id && user.id === authUser.id) return true;
  if (authUser.email && user.email?.toLowerCase() === authUser.email.toLowerCase()) return true;
  return false;
};

const UploadShareTargetFields = ({ selectedUsers = [], onSelectedUsersChange }) => {
  const { user: authUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [draftUsers, setDraftUsers] = useState([]);

  const fetchOrganizations = useCallback(async () => {
    setOrgsLoading(true);
    try {
      const res = await axios.get(`${API}/organizations`, {
        params: { page: 1, limit: 100 },
      });
      setOrganizations(res.data?.organizations ?? res.data?.items ?? []);
    } catch {
      toast.error("Failed to load organizations");
      setOrganizations([]);
    } finally {
      setOrgsLoading(false);
    }
  }, []);

  const fetchOrgUsers = useCallback(async (organizationId) => {
    if (!organizationId) {
      setUsers([]);
      return;
    }
    setUsersLoading(true);
    try {
      const res = await axios.get(`${API}/users`, {
        params: {
          organization_id: organizationId,
          page: 1,
          limit: 200,
        },
      });
      const items = res.data?.users ?? res.data?.items ?? [];
      setUsers(Array.isArray(items) ? items.filter((u) => u?.id) : []);
    } catch {
      toast.error("Failed to load users");
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setUserSearch("");
    setDraftUsers(Array.isArray(selectedUsers) ? selectedUsers.filter((u) => u?.id) : []);
    setUsers([]);
    const initialOrgId = String(selectedUsers?.[0]?.organization_id ?? "");
    setSelectedOrganizationId(initialOrgId);
    fetchOrganizations();
    // Initialize from current recipients only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fetchOrganizations]);

  useEffect(() => {
    if (!open || orgsLoading) return;
    if (organizations.length !== 1) return;
    const onlyId = getOrgId(organizations[0]);
    if (onlyId && !selectedOrganizationId) {
      setSelectedOrganizationId(onlyId);
    }
  }, [open, orgsLoading, organizations, selectedOrganizationId]);

  useEffect(() => {
    if (!open) return;
    if (!selectedOrganizationId) {
      setUsers([]);
      return;
    }
    fetchOrgUsers(selectedOrganizationId);
  }, [open, selectedOrganizationId, fetchOrgUsers]);

  const filteredUsers = useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    const annotated = users.filter((u) => !isSameAuthUser(authUser, u));
    if (!query) return annotated;
    return annotated.filter(
      (u) =>
        u.name?.toLowerCase().includes(query)
        || u.email?.toLowerCase().includes(query),
    );
  }, [users, userSearch, authUser]);

  const handleOrganizationChange = (orgId) => {
    setSelectedOrganizationId(orgId);
    setUserSearch("");
  };

  const handleToggleUser = (user) => {
    if (!user?.id) return;
    setDraftUsers((prev) => {
      const exists = prev.some((u) => u.id === user.id);
      if (exists) return prev.filter((u) => u.id !== user.id);
      return [...prev, { ...user, organization_id: user.organization_id || selectedOrganizationId }];
    });
  };

  const handleConfirm = () => {
    onSelectedUsersChange?.(draftUsers);
    setOpen(false);
  };

  const selectedCount = Array.isArray(selectedUsers) ? selectedUsers.length : 0;

  return (
    <div className="flex min-w-0 flex-shrink-0 items-end">
      <button
        type="button"
        data-testid="upload-share-btn"
        onClick={() => setOpen(true)}
        className="relative inline-flex h-10 w-auto items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[var(--xl-line2)] bg-[var(--xl-surf)] px-3 text-sm text-black hover:bg-black/[0.04]"
        title={selectedCount > 0 ? `Share with ${selectedCount} user${selectedCount === 1 ? "" : "s"}` : "Upload on behalf"}
        aria-label={selectedCount > 0 ? `Change share recipients (${selectedCount})` : "Upload on behalf"}
      >
        <Share2 className="h-4 w-4 shrink-0" />
        <span>Upload on behalf</span>
        {selectedCount > 0 ? (
          <span className="grid h-4 min-w-4 place-items-center rounded-full bg-[var(--xl-primary)] px-1 text-[10px] font-medium text-white">
            {selectedCount}
          </span>
        ) : null}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="upload-share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-4 w-4 text-[var(--xl-primary)]" />
              Share Study
            </DialogTitle>
            <DialogDescription>
              Select one or more users. The study will be shared with each after it uploads successfully.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Organization</Label>
              <Select
                value={selectedOrganizationId || undefined}
                onValueChange={handleOrganizationChange}
                disabled={orgsLoading}
              >
                <SelectTrigger data-testid="upload-share-org-select">
                  <SelectValue placeholder={orgsLoading ? "Loading organizations…" : "Select organization"} />
                </SelectTrigger>
                <SelectContent
                  side="bottom"
                  align="start"
                  sideOffset={4}
                  avoidCollisions={false}
                  className="max-h-60"
                >
                  {organizations.length === 0 && !orgsLoading ? (
                    <SelectItem value="__none__" disabled>
                      No organizations found
                    </SelectItem>
                  ) : (
                    organizations.map((org) => {
                      const orgId = getOrgId(org);
                      const label = getOrgLabel(org) || orgId;
                      if (!orgId) return null;
                      return (
                        <SelectItem key={orgId} value={orgId}>
                          {label}
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={selectedOrganizationId ? "Search by name or email…" : "Select an organization first"}
                className="pl-8 text-sm"
                value={userSearch}
                disabled={!selectedOrganizationId}
                onChange={(e) => setUserSearch(e.target.value)}
              />
            </div>

            {draftUsers.length > 0 ? (
              <p className="text-xs text-muted-foreground">
                Selected: {draftUsers.map((u) => u.name || u.email).join(", ")}
              </p>
            ) : null}

            {!selectedOrganizationId ? (
              <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-border py-8 text-center">
                <Building2 className="mb-2 h-5 w-5 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">Select an organization to view its users</p>
              </div>
            ) : usersLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <ScrollArea className="h-48 rounded-md border border-border">
                {filteredUsers.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">No users found</p>
                ) : (
                  <div className="p-1">
                    {filteredUsers.map((u) => {
                      const isSelected = draftUsers.some((selected) => selected.id === u.id);
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => handleToggleUser(u)}
                          className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors ${
                            isSelected
                              ? "border border-primary/30 bg-primary/10"
                              : "hover:bg-muted/50"
                          }`}
                        >
                          <div className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-full bg-secondary text-[11px] font-semibold uppercase text-muted-foreground">
                            {u.name?.charAt(0) ?? "?"}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">{u.name}</p>
                            <p className="truncate text-[11px] text-muted-foreground">{u.email}</p>
                          </div>
                          {isSelected ? (
                            <Check className="h-4 w-4 flex-shrink-0 text-primary" />
                          ) : null}
                          {u.role ? (
                            <Badge variant="outline" className="flex-shrink-0 text-[10px]">
                              {ROLE_LABELS[u.role] ?? u.role.replace(/_/g, " ")}
                            </Badge>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            )}

            <Button
              className="w-full gap-2"
              onClick={handleConfirm}
              disabled={draftUsers.length === 0}
              data-testid="upload-share-confirm"
            >
              <Share2 className="h-4 w-4" />
              {draftUsers.length === 0
                ? "Select users"
                : draftUsers.length === 1
                  ? `Share with ${draftUsers[0].name}`
                  : `Share with ${draftUsers.length} users`}
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UploadShareTargetFields;
